import type { StyleRow, CustomFieldRowEntry } from './stylesFixtureData';

/**
 * Deterministic fixtures for the "Bulk edit styles: validation" epic (DL-62595) —
 * mandatory-for-export gating, stale/inactive custom-field references on untouched fields,
 * the Textbox CF max-char limit, and the Brand ↔ Supplier Contact access conflict. Isolable via
 * `?search=BulkValidationProbe` (unique substring, absent from every other fixture name), with a
 * sub-word per scenario for further narrowing (e.g. `?search=BulkValidationProbe Stale`).
 *
 * Split out of `styles.ts` (already over the repo's 800-line `max-lines` guideline) — same
 * reasoning as `bulkUpdateProbeFixtureData.ts` for items. `styles.ts` imports
 * `bulkEditValidationFixtureStyles` from HERE (to splice into its seed data), so this file
 * deliberately does NOT import back from `styles.ts` (would be circular) — the small brand/
 * supplier/contact literals below are self-contained, matching the real IDs `styles.ts` uses
 * (`brandOptions[0]`'s id 29 / "Brand A", supplier id 44 / "Ningbo garment supplier", contact
 * `d47e406b-d5db-4787-ade6-7225780b5b5c` / "Sara Johnson", season id 26 / "AW25", group id 71 /
 * "Menswear") so a fixture style here looks identical to one `styles.ts` would have generated.
 */

const FIXTURE_BRAND = { id: 29, userDefinedId: '1', name: 'Brand A' };
const FIXTURE_SUPPLIER = { id: 44, customId: 'S-44', name: 'Ningbo garment supplier' };
const FIXTURE_CONTACT_A = { id: 'd47e406b-d5db-4787-ade6-7225780b5b5c', name: 'Sara Johnson' };
const FIXTURE_CONTACT_B = { id: 'a12b3c4d-e5f6-7890-abcd-ef1234567890', name: 'Mike Chen' };
const FIXTURE_SEASON = { id: 26, customId: 'AW25', name: 'AW25', project: '' };
const FIXTURE_GROUP = { id: 71, customId: 'MEN', name: 'Menswear' };

const bulkValidationProbeBase = {
  description: null,
  logo: '',
  season: FIXTURE_SEASON,
  group: FIXTURE_GROUP,
  categories: [] as StyleRow['categories'],
  styleColors: [] as StyleRow['styleColors'],
  properties: [] as string[],
  lastUpdated: '2026-01-10T09:00:00',
};

/**
 * The extra mandatory-for-export CF definition (`id: 90`, "Export Note") layered on top of the
 * baseline `styleCustomFieldDefinitions` via `apiTransform({ url: '/api/meta/styles/custom-fields',
 * data: [...styleCustomFieldDefinitions, mandatoryExportCustomFieldDefinition] })` — none of the
 * baseline definitions are `isMandatory: true` today. Mirrors
 * `bulkUpdateProbeFixtureData.ts`'s `mandatoryExportCustomFieldDefinition` for items.
 */
export const mandatoryExportCustomFieldDefinition = {
  id: 90,
  name: 'Export Note',
  position: 8,
  type: 2, // Text
  isMandatory: true,
  isSupplierEditable: true,
  internalOnly: false,
  maxChar: 500,
  isMultiLine: true,
};

const exportNoteEntry = (value: string): CustomFieldRowEntry => ({
  id: mandatoryExportCustomFieldDefinition.id,
  type: 'text',
  value: { id: 0, value },
});

/**
 * Testmo #1577252 (DL-62628) / #1577262 (DL-62629) — mandatory-for-export field validated (and
 * uncleatable via the UI's "Set empty" affordance, dl-60044's preventive guard — see the sibling
 * Items spec `itemBulkEditFieldValidation.spec.ts` this mirrors) when the style IS Ready for
 * Export. Two items with the same initial "Ready note" value per pair (a real bulk-edit
 * selection, not a single style standing in for "bulk").
 */
export const bulkValidationProbeExportReadyA: StyleRow = {
  ...bulkValidationProbeBase,
  id: 90300,
  name: 'BulkValidationProbe Export Ready A',
  customId: 'ST-90300',
  state: 'unpublished',
  brand: {
    id: FIXTURE_BRAND.id,
    customId: FIXTURE_BRAND.userDefinedId,
    name: FIXTURE_BRAND.name,
    contactPersonId: FIXTURE_CONTACT_A.id,
    contactPersonName: FIXTURE_CONTACT_A.name,
  },
  supplier: {
    id: FIXTURE_SUPPLIER.id,
    customId: FIXTURE_SUPPLIER.customId,
    name: FIXTURE_SUPPLIER.name,
    contactPersonId: FIXTURE_CONTACT_B.id,
    contactPersonName: FIXTURE_CONTACT_B.name,
  },
  customFields: [exportNoteEntry('Ready note')],
  readyForExport: true,
};
export const bulkValidationProbeExportReadyB: StyleRow = {
  ...bulkValidationProbeExportReadyA,
  id: 90301,
  name: 'BulkValidationProbe Export Ready B',
  customId: 'ST-90301',
};

/** Same mandatory field, but NOT Ready for export — the field must be freely clearable, with
 *  no validation error, contrasting the Ready pair above. */
export const bulkValidationProbeExportNotReadyA: StyleRow = {
  ...bulkValidationProbeExportReadyA,
  id: 90302,
  name: 'BulkValidationProbe Export Not Ready A',
  customId: 'ST-90302',
  customFields: [exportNoteEntry('Not ready note')],
  readyForExport: false,
};
export const bulkValidationProbeExportNotReadyB: StyleRow = {
  ...bulkValidationProbeExportNotReadyA,
  id: 90303,
  name: 'BulkValidationProbe Export Not Ready B',
  customId: 'ST-90303',
};

/**
 * Testmo #1577261 (DL-62630) — untouched mandatory-for-export field does not trigger
 * validation. Both Ready for export, each with a DIFFERENT Export Note value (not identical) so
 * "untouched fields are not collapsed/overwritten" has something item-specific to verify after a
 * change to an unrelated field (Season) goes through.
 */
export const bulkValidationProbeExportUntouchedA: StyleRow = {
  ...bulkValidationProbeExportReadyA,
  id: 90304,
  name: 'BulkValidationProbe Export Untouched A',
  customId: 'ST-90304',
  customFields: [exportNoteEntry('Untouched note A')],
  readyForExport: true,
};
export const bulkValidationProbeExportUntouchedB: StyleRow = {
  ...bulkValidationProbeExportUntouchedA,
  id: 90305,
  name: 'BulkValidationProbe Export Untouched B',
  customId: 'ST-90305',
  customFields: [exportNoteEntry('Untouched note B')],
};

/**
 * Stale/orphaned custom-field references — this codebase's CF/lookup wire contract carries no
 * `isActive` flag on an individual allowed value (verified: neither `CustomFieldDefinition`
 * (`features/styles/types/Style.ts`) nor the `/api/meta/styles/custom-fields` mock shape has
 * one). The functional equivalent of "a value went inactive but is still assigned" is a CF entry
 * whose `value.id` (or one entry of a multi-select `values[]`) no longer matches any option in
 * that field's CURRENT `allowedValues` list (e.g. removed from the definition after
 * assignment) — `getSharedBulkEditFieldValue` (`bulkEditSharedValue.ts`) renders it from the
 * row's own persisted label regardless, so it displays fine but can never be "re-selected" from
 * the live option list. `id: 79999` / `79998` below are deliberately outside CF703's
 * (`7031`-`7033`) and CF704's (`7041`-`7044`) real ranges.
 *
 * Testmo #1577260 (DL-62631) — untouched field with such a stale value does not trigger a
 * validation error (single style; a second, identically-stale item pairs it into a real bulk
 * selection).
 */
export const bulkValidationProbeStaleUniformA: StyleRow = {
  ...bulkValidationProbeBase,
  id: 90310,
  name: 'BulkValidationProbe Stale Uniform A',
  customId: 'ST-90310',
  state: 'unpublished',
  brand: {
    id: FIXTURE_BRAND.id,
    customId: FIXTURE_BRAND.userDefinedId,
    name: FIXTURE_BRAND.name,
    contactPersonId: FIXTURE_CONTACT_A.id,
    contactPersonName: FIXTURE_CONTACT_A.name,
  },
  supplier: {
    id: FIXTURE_SUPPLIER.id,
    customId: FIXTURE_SUPPLIER.customId,
    name: FIXTURE_SUPPLIER.name,
    contactPersonId: FIXTURE_CONTACT_B.id,
    contactPersonName: FIXTURE_CONTACT_B.name,
  },
  customFields: [{ id: 703, type: 'allowedvalue', value: { id: 79999, value: 'Legacy Fit' } }],
  readyForExport: false,
};
export const bulkValidationProbeStaleUniformB: StyleRow = {
  ...bulkValidationProbeStaleUniformA,
  id: 90311,
  name: 'BulkValidationProbe Stale Uniform B',
  customId: 'ST-90311',
};

/**
 * Testmo #1577271 (DL-62632) — a multi-style selection where SOME styles carry a stale value on
 * a field and others carry a live one; leaving the field untouched must not block the edit for
 * any style in the selection.
 */
export const bulkValidationProbeStaleMixedA: StyleRow = {
  ...bulkValidationProbeStaleUniformA,
  id: 90312,
  name: 'BulkValidationProbe Stale Mixed A',
  customId: 'ST-90312',
  customFields: [{ id: 703, type: 'allowedvalue', value: { id: 79999, value: 'Legacy Fit' } }],
};
export const bulkValidationProbeStaleMixedB: StyleRow = {
  ...bulkValidationProbeStaleUniformA,
  id: 90313,
  name: 'BulkValidationProbe Stale Mixed B',
  customId: 'ST-90313',
  customFields: [{ id: 703, type: 'allowedvalue', value: { id: 7031, value: 'Slim' } }],
};

/**
 * Testmo #1577273 (DL-62633) — a single untouched field mixing a live and a stale value within
 * the SAME multi-select custom field (Fabric Blend, CF 704) must not block submit either. Two
 * items with the identical mix (a real bulk selection, not a single style).
 */
export const bulkValidationProbeStaleMultiselectA: StyleRow = {
  ...bulkValidationProbeStaleUniformA,
  id: 90314,
  name: 'BulkValidationProbe Stale Multiselect A',
  customId: 'ST-90314',
  customFields: [
    {
      id: 704,
      type: 'allowedvalue',
      values: [
        { id: 7041, value: 'Cotton' },
        { id: 79998, value: 'Legacy Blend' },
      ],
    },
  ],
};
export const bulkValidationProbeStaleMultiselectB: StyleRow = {
  ...bulkValidationProbeStaleMultiselectA,
  id: 90315,
  name: 'BulkValidationProbe Stale Multiselect B',
  customId: 'ST-90315',
};

/**
 * Testmo #1577243 (DL-62634) — Textbox CF max-char validation message. No dedicated CF needed:
 * `styleCustomFieldDefinitions`'s CF 701 ("Fabric Notes") already carries `maxChar: 255`. Two
 * plain fixture styles (no special custom-field values) so the scenario doesn't ride on shared
 * state from any other spec's fixtures.
 */
export const bulkValidationProbeMaxCharA: StyleRow = {
  ...bulkValidationProbeBase,
  id: 90320,
  name: 'BulkValidationProbe MaxChar A',
  customId: 'ST-90320',
  state: 'unpublished',
  brand: {
    id: FIXTURE_BRAND.id,
    customId: FIXTURE_BRAND.userDefinedId,
    name: FIXTURE_BRAND.name,
    contactPersonId: FIXTURE_CONTACT_A.id,
    contactPersonName: FIXTURE_CONTACT_A.name,
  },
  supplier: {
    id: FIXTURE_SUPPLIER.id,
    customId: FIXTURE_SUPPLIER.customId,
    name: FIXTURE_SUPPLIER.name,
    contactPersonId: FIXTURE_CONTACT_B.id,
    contactPersonName: FIXTURE_CONTACT_B.name,
  },
  customFields: [],
  readyForExport: false,
};
export const bulkValidationProbeMaxCharB: StyleRow = {
  ...bulkValidationProbeMaxCharA,
  id: 90321,
  name: 'BulkValidationProbe MaxChar B',
  customId: 'ST-90321',
};

/**
 * Testmo #1577244 (DL-62635) — "Season/Brand Contact conflict" in the Testmo prose maps onto
 * this codebase's real access-conflict guard: `useBulkEditBrandSupplier`'s
 * `bulkEditBrandSupplierErrors` (`brandSupplierBulkEditAccess.ts`), which fires when the
 * selection's Brand and Supplier Contact do not have access to each other — not a literal
 * "Season" field, which carries no such cross-field access rule anywhere in the codebase
 * (verified by grep — `season` never appears in `bulkEdit/utils` or `bulkEdit/hooks`). This is
 * the one real "two related fields, access-limited combination, error message(s)" flow the
 * Styles bulk-edit modal has, so this spec exercises it and asserts exactly one message renders
 * per conflicting field (never duplicated), matching the epic's "de-duplicated" intent.
 *
 * Both styles share supplier id 44 ("Ningbo garment supplier") and its first contact ("Sara
 * Johnson"), on `brandOptions[0]`. The sibling spec overrides `GET /api/organizations/44/users`
 * via `apiTransform` to give both of that supplier's contacts a brand-restricted
 * `accessToAllBrands: false` so the conflict is actually reachable (the baseline mock always
 * returns `accessToAllBrands: true`, see `styles.ts`'s `/api/organizations/:orgId/users`).
 */
export const bulkValidationProbeConflictA: StyleRow = {
  ...bulkValidationProbeBase,
  id: 90330,
  name: 'BulkValidationProbe Conflict A',
  customId: 'ST-90330',
  state: 'unpublished',
  brand: {
    id: FIXTURE_BRAND.id,
    customId: FIXTURE_BRAND.userDefinedId,
    name: FIXTURE_BRAND.name,
    contactPersonId: FIXTURE_CONTACT_A.id,
    contactPersonName: FIXTURE_CONTACT_A.name,
  },
  supplier: {
    id: 44,
    customId: 'S-44',
    name: 'Ningbo garment supplier',
    contactPersonId: FIXTURE_CONTACT_A.id,
    contactPersonName: FIXTURE_CONTACT_A.name,
  },
  customFields: [],
  readyForExport: false,
};
export const bulkValidationProbeConflictB: StyleRow = {
  ...bulkValidationProbeConflictA,
  id: 90331,
  name: 'BulkValidationProbe Conflict B',
  customId: 'ST-90331',
};

export const bulkEditValidationFixtureStyles: StyleRow[] = [
  bulkValidationProbeExportReadyA,
  bulkValidationProbeExportReadyB,
  bulkValidationProbeExportNotReadyA,
  bulkValidationProbeExportNotReadyB,
  bulkValidationProbeExportUntouchedA,
  bulkValidationProbeExportUntouchedB,
  bulkValidationProbeStaleUniformA,
  bulkValidationProbeStaleUniformB,
  bulkValidationProbeStaleMixedA,
  bulkValidationProbeStaleMixedB,
  bulkValidationProbeStaleMultiselectA,
  bulkValidationProbeStaleMultiselectB,
  bulkValidationProbeMaxCharA,
  bulkValidationProbeMaxCharB,
  bulkValidationProbeConflictA,
  bulkValidationProbeConflictB,
];
